import type { FamilyPlanConfig, PaymentGateway, UserSubscription } from '@types/index';

const CHECKOUT_DRAFT_KEY = 'encycgames.subscriptionCheckout';

export interface SubscriptionCheckoutDraft {
  offerId: string;
  family?: FamilyPlanConfig;
  amount: number;
  currency: string;
  planLabel: string;
  summary?: string;
  listUsd?: number;
  discountPercent?: number;
  earlyBirdActive?: boolean;
  provider: PaymentGateway;
}

export function writeSubscriptionCheckoutDraft(draft: SubscriptionCheckoutDraft): void {
  try {
    sessionStorage.setItem(CHECKOUT_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    /* private mode / quota */
  }
}

export function readSubscriptionCheckoutDraft(): SubscriptionCheckoutDraft | null {
  try {
    const raw = sessionStorage.getItem(CHECKOUT_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SubscriptionCheckoutDraft;
    if (!parsed?.offerId || parsed.amount == null) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearSubscriptionCheckoutDraft(): void {
  try {
    sessionStorage.removeItem(CHECKOUT_DRAFT_KEY);
  } catch {
    /* ignore */
  }
}

export interface SubscriptionCheckout {
  mode: 'stripe' | 'razorpay';
  provider: string;
  subscriptionId?: string;
  stripeUrl?: string;
  stripeSessionId?: string;
  razorpayOrderId?: string;
  razorpayKeyId?: string;
  amount?: number;
  currency?: string;
  description?: string;
  name?: string;
  email?: string;
}

function loadRazorpayScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if ((window as unknown as { Razorpay?: unknown }).Razorpay) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Could not open Razorpay checkout'));
    document.body.appendChild(script);
  });
}

export async function openSubscriptionCheckout(
  checkout: SubscriptionCheckout,
  onRazorpayPaid: (payload: {
    razorpay_order_id: string;
    razorpay_payment_id: string;
    razorpay_signature: string;
  }) => Promise<UserSubscription>,
): Promise<'redirect' | UserSubscription> {
  if (checkout.mode === 'stripe' && checkout.stripeUrl) {
    window.location.assign(checkout.stripeUrl);
    return 'redirect';
  }
  if (checkout.mode === 'razorpay' && checkout.razorpayOrderId && checkout.razorpayKeyId) {
    await loadRazorpayScript();
    const RazorpayCtor = (window as unknown as {
      Razorpay: new (options: Record<string, unknown>) => { open: () => void };
    }).Razorpay;
    return new Promise((resolve, reject) => {
      const rzp = new RazorpayCtor({
        key: checkout.razorpayKeyId,
        amount: Math.round(Number(checkout.amount || 0) * 100),
        currency: checkout.currency || 'INR',
        name: 'EncycGames',
        description: checkout.description || 'Yearly subscription',
        order_id: checkout.razorpayOrderId,
        prefill: {
          name: checkout.name || '',
          email: checkout.email || '',
        },
        handler: async (response: {
          razorpay_order_id: string;
          razorpay_payment_id: string;
          razorpay_signature: string;
        }) => {
          try {
            resolve(await onRazorpayPaid(response));
          } catch (error) {
            reject(error);
          }
        },
        modal: {
          ondismiss: () => reject(new Error('Payment window closed before completing.')),
        },
      });
      rzp.open();
    });
  }
  throw new Error('Payment provider not connected yet.');
}
